import {
  checkAttachmentUpload,
  isConversationAttachment,
} from './direct-call-attachments';
import type {
  AttachmentUpload,
  ConversationAttachment,
} from './direct-call-attachments';
import type { HumanCallRequest } from './direct-call-http';

/** The authenticated, deadline-bounded transport of HumanConversationHttp. */
export type AttachmentSend = (
  path: string,
  method: string,
  signal: AbortSignal,
  body?: unknown,
  deadlineMs?: number
) => Promise<Awaited<ReturnType<HumanCallRequest>>>;

// A 15 MB upload on a cellular link outlives the 15 s text deadline.
const ATTACHMENT_DEADLINE_MS = 120000;
const ATTACHMENT_ERROR_CODES = new Set([
  'attachment-too-large',
  'attachment-type-not-allowed',
  'attachment-content-mismatch',
  'attachment-empty',
  'invalid-file-name',
]);

function attachmentError(code: string | undefined): Error {
  return Object.assign(new Error('Attachment was not accepted'), { code });
}

/**
 * The events POST body: `to` (Bug 35509, the addressed seats; omitted means everyone)
 * and `attachmentIds` (WI 35510, uploads this turn claims) only when present.
 */
export function conversationEventBody(
  content: string,
  to: readonly string[] | undefined,
  attachmentIds: readonly string[]
): { content: string; to?: string[]; attachmentIds?: string[] } {
  return {
    content,
    ...(to?.length ? { to: [...to] } : {}),
    ...(attachmentIds.length ? { attachmentIds: [...attachmentIds] } : {}),
  };
}

/** WI 35510: POST a pending upload. `conversation` is already validated and encoded. */
export async function uploadConversationAttachment(
  send: AttachmentSend,
  conversation: string,
  upload: AttachmentUpload,
  signal: AbortSignal
): Promise<ConversationAttachment> {
  const refused = checkAttachmentUpload(upload);
  if (refused) throw attachmentError(refused);
  const response = await send(
    `/conversations/${conversation}/attachments`,
    'POST',
    signal,
    { fileName: upload.fileName, dataBase64: upload.dataBase64 },
    ATTACHMENT_DEADLINE_MS
  );
  if (!response.ok) {
    // Only a known code crosses to the UI, never server text.
    const body = (await response.json().catch(() => null)) as {
      error?: unknown;
    } | null;
    const code = typeof body?.error === 'string' ? body.error : undefined;
    throw attachmentError(
      (response.status === 400 || response.status === 413) &&
        code &&
        ATTACHMENT_ERROR_CODES.has(code)
        ? code
        : undefined
    );
  }
  const stored = await response.json();
  if (!isConversationAttachment(stored)) throw new Error('Invalid attachment');
  return stored;
}

/** WI 35510: GET the bytes of a claimed attachment. */
export async function downloadConversationAttachment(
  send: AttachmentSend,
  conversation: string,
  attachmentId: string,
  signal: AbortSignal
): Promise<ArrayBuffer> {
  if (!/^[a-f0-9]{32}$/i.test(attachmentId))
    throw new Error('Invalid attachment');
  const response = await send(
    `/conversations/${conversation}/attachments/${attachmentId}`,
    'GET',
    signal,
    undefined,
    ATTACHMENT_DEADLINE_MS
  );
  if (!response.ok)
    throw new Error(`Conversation request failed (${response.status})`);
  if (!response.arrayBuffer)
    throw new Error('This platform cannot read binary attachments');
  return response.arrayBuffer();
}
